import { useState } from 'react'

import { styled } from 'styled-components'

import {
  Tag,
  TagFormat,
  TagType,
  Tooltip,
  useIsContentTruncated,
} from '@b-stock/fe-core/bstock-react'
import { ListingEnums } from '@b-stock/fe-core/locale'
import { ItemConditionDtoItemConditionsEnum } from '@b-stock/listing-api-client'

import type { SearchResult } from '@/queries/listingSearchQuery'

import isBstockBlue from '../isBstockBlue'

const StyledTag = styled(Tag)`
  max-width: 100%;
`

const PipeSeparatedList = styled.div`
  display: flex;
  gap: 0rem 0.5rem;
  flex-wrap: wrap;
`

const TruncatedPipeSeparatedList = styled(PipeSeparatedList)`
  max-width: 100%;
  white-space: nowrap;
  flex-wrap: nowrap;
  & > div {
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 1;
    max-width: 100%;
    &:first-child {
      flex-shrink: 0;
    }
  }
`

export const transformCondition = (condition: string) => {
  // Ideally, we'd get the raw ItemConditionDtoItemConditionsEnum value for condition...
  // but that's not what the Search Service sends us in the AllListingsListing record.
  // It sends an already transformed value (and "Mixed" if there are multiple values
  // for a listing, instead of giving us an array with every condition).
  // This is done partially because of search requirements (we want the AI search to
  // do its analysis based on the human-readable values, not the internal ones), and
  // because we are re-using the same, already transformed data for Search, Filter,
  // and Display purposes.
  // This is further complicated by the fact that Enterprise uses different data,
  // so the 3mp enum values aren't necessarily a perfect fit for enterprise listings.
  // So, for now at least, we trust the Search Service to "do the right thing" (mostly)
  // and give us end-user presentable values for condition.
  // See also: `toConditionDisplayName` in `components/SearchFilters/toDisplayNames.tsx`

  // Search service (currently) sends us "Brand New" instead of "New", but we want to show "New"
  if (condition === 'Brand New') {
    return 'New'
  }

  // Search service sends us "Mixed", but we want to show "Mixed Condition" to disambiguate
  //  since "Mixed" here could easily be referring to anything since nothing explicitly says
  // this tag is showing condition info.
  // We don't need "Condition" for other conditions, because the other ones are easier to
  // know are conditions based on their text already.
  // Everywhere else we show "Mixed", we don't need the "Condition" suffix because we have a
  // separate Condition label already.
  // Related UX thread: https://www.figma.com/design/TdUz3Zv9bju6FFhrKD25GC?node-id=773-315352&m=dev#1283820698
  if (condition === 'Mixed') {
    return 'Mixed Condition'
  }

  // Everything else can be shown exactly as it is sent by Search Service
  // (it's already been converted into human-readable form)
  return condition
}

const InventoryTypeAndCondition = ({
  listing,
}: {
  listing: Pick<
    SearchResult,
    'condition' | 'displayedCondition' | 'inventoryType'
  >
}) => {
  const unspecifiedCondition = ListingEnums.ItemCondition.useFormatted(
    ItemConditionDtoItemConditionsEnum.Unspecified
  )
  // Only suppress the tag when Salvage is the listing's SOLE condition. A
  // listing with Salvage mixed among other conditions must still show the
  // "Mixed Condition" tag that displayedCondition already resolves to.
  const isSalvage =
    listing.condition.length === 1 &&
    listing.condition.some(
      (condition) =>
        // We'll get the translated "Salvage" value... but just to be
        // extra safe in case we ever get "salvage" or "SALVAGE" instead
        // TODO: TBD-3167 Requirements potentially in flux - https://www.figma.com/design/TdUz3Zv9bju6FFhrKD25GC?node-id=759-353637&m=dev#1297129998
        condition.toUpperCase() === ItemConditionDtoItemConditionsEnum.Salvage
    )

  // Intentionally showing just the dominant (or salvage) condition, and
  // ignoring all the rest (including no "+N More" suffix, which we had
  // in earlier versions).
  // See https://www.figma.com/design/TdUz3Zv9bju6FFhrKD25GC?node-id=773-315352&m=dev#1283820698
  // No technical reason why this can't be added back in if product changes
  // their mind though and starts wanting to distinguish between single
  // and multiple condition listings.
  const dominantCondition = !isSalvage
    ? transformCondition(listing.displayedCondition || unspecifiedCondition)
    : ''

  const content = (
    <>
      {listing.inventoryType ? (
        // inventoryType is required in 3mp, but not in enterprise
        // Related UX discussion: https://www.figma.com/design/TdUz3Zv9bju6FFhrKD25GC?node-id=773-272445&m=dev#1297158218
        <>
          <div data-testid="inventory-type">{listing.inventoryType}</div>
          {dominantCondition ? <div>|</div> : ''}
        </>
      ) : null}
      <div data-testid="condition">{dominantCondition}</div>
    </>
  )
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null)

  // 99.9% of the time, if anything is truncated, it will be the condition, so
  // just checking isLastChildTagTruncated should be sufficient... but the
  // extra check ensures that we cover the (extremely unlikely, but not
  // strictly prevented) edge case of an excessively long Inventory Type
  // value AND an empty string condition value (if we had a long Inventory
  // Type and a non-empty condition, then condition would still be truncated
  // to a width of 0px, it's only when condition empty that it would be 0px
  // width and have 0px of space available, and therefore not be considered
  // truncated).
  const isFirstTruncated = useIsContentTruncated(
    // Either Inventory Type or, if no Inventory Type, condition
    contentRef?.firstElementChild ?? null
  )
  const isLastTruncated = useIsContentTruncated(
    // Always Condition
    contentRef?.lastElementChild ?? null
  )

  return (
    <StyledTag
      data-testid="inventory-type-and-condition"
      format={TagFormat.combinedAttr}
      type={isBstockBlue(listing) ? TagType.info : TagType.normal}
    >
      <TruncatedPipeSeparatedList ref={setContentRef}>
        {content}
      </TruncatedPipeSeparatedList>
      {isFirstTruncated || isLastTruncated ? (
        <Tooltip referenceElement={contentRef}>
          <PipeSeparatedList>{content}</PipeSeparatedList>
        </Tooltip>
      ) : null}
    </StyledTag>
  )
}

export default InventoryTypeAndCondition
